import { queryOptions, skipToken } from '@tanstack/react-query'
import { fetchChildren, fetchHousehold, type Household } from '../api/households'
import { fetchShippingAddresses } from '../api/shipping-addresses'
import { fetchSubscription, fetchSubscriptions, type Subscription } from '../api/subscriptions'
import { fetchUser } from '../api/users'
import { currentAuth } from '../state/session'

/**
 * The portal's server state, defined once per resource so a mutation can
 * write back exactly what it changed. Auth is read at fetch time from the
 * session store, the same source every other call in the bundle uses, so a
 * query never captures a token that a sign-out has since dropped.
 *
 * Keys are rooted by what they hold, so a write can name exactly the queries
 * it changes rather than matching a prefix that could grow to mean more.
 */

export function userQuery(id: string) {
  return queryOptions({
    queryKey: ['users', id],
    queryFn: ({ signal }) => fetchUser(id, { auth: currentAuth(), signal }),
  })
}

export function householdQuery(userId: string) {
  return queryOptions({
    queryKey: ['users', userId, 'household'],
    queryFn: ({ signal }) => fetchHousehold(userId, { auth: currentAuth(), signal }),
  })
}

/** Every household's children, for a write that must reach a child wherever they are listed. */
export const CHILDREN_LISTS = { queryKey: ['children'] }

/**
 * The children of a household. Accepts the household query's result directly
 * — undefined while it loads, null for a parent who owns none — and waits
 * rather than firing a request that could not name an organization.
 */
export function childrenQuery(household: Household | null | undefined) {
  return queryOptions({
    queryKey: ['children', household?.id ?? null],
    queryFn: household
      ? ({ signal }) => fetchChildren(household, { auth: currentAuth(), signal })
      : skipToken,
  })
}

/** The subscriptions of a household's children: plan and status, nothing that needs Recharge. */
export function subscriptionsQuery(household: Household | null | undefined) {
  return queryOptions({
    queryKey: ['household-subscriptions', household?.id ?? null],
    queryFn: household
      ? ({ signal }) => fetchSubscriptions(household, { auth: currentAuth(), signal })
      : skipToken,
  })
}

/** One subscription as Recharge has it now: what it will next charge, and when. */
export function subscriptionQuery(id: string) {
  return queryOptions({
    queryKey: ['subscriptions', id],
    queryFn: ({ signal }) => fetchSubscription(id, { auth: currentAuth(), signal }),
  })
}

export function shippingAddressesKey(household: Household) {
  return ['shipping-addresses', household.id] as const
}

/**
 * The household's address book, read through one of its subscriptions. Every
 * child's subscription belongs to the one purchaser, so the book is the same
 * whichever subscription reads it and is fetched once per household; each
 * subscription's own address is the one that lists it. Waits for a
 * subscription Recharge knows, since the API has no book for one it does not.
 */
export function shippingAddressesQuery(household: Household, subscription: Subscription | null) {
  return queryOptions({
    queryKey: shippingAddressesKey(household),
    queryFn: subscription
      ? ({ signal }) => fetchShippingAddresses(subscription.id, { auth: currentAuth(), signal })
      : skipToken,
  })
}
